// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Il test di tracciabilità"
// Centro assistenza → guida per amministratori, solo piano TRACE.
//
// E' la schermata per cui esiste tutto il modulo: da un lotto risale a monte o
// scende a valle, e produce il documento da mettere in mano a un ispettore.
//
// Schermate RICOSTRUITE (vedi la nota in atomi.js). Etichette dal codice.
//
// Riferimenti nel codice, per chi dovra' aggiornarla:
//   Schermata ..... App.js, TestTracciabilita — due schede:
//                   "Forward · MP → PF" (ciano) e "Backward · PF → MP" (viola)
//   Forward ....... GET /test-forward?lottoMp=  — parte da un lotto di materia
//                   prima e arriva FINO AI CLIENTI: lotto MP -> lavorazioni ->
//                   lotti PF (con quantita_uscita e residua) -> bolle di uscita
//                   con il nome del cliente.
//   Backward ...... GET /test-backward?lottoPf= — dal lotto di prodotto finito
//                   a materie prime, semilavorati, fasi, movimenti, bolle.
//   RICERCA PARZIALE: tutte e due usano ILIKE %testo%, e cercano anche nel NOME
//                   del prodotto e della ricetta. Scrivere "Pane" restituisce
//                   tutte le lavorazioni di pane. E' voluto, ma va detto.
//   Stampa ........ DUE schede, una per direzione (dal 19/08/2026; prima
//                   esisteva solo il backward). CSS e intestazione stanno in
//                   CSS_SCHEDA e intestazioneScheda, condivisi: due copie
//                   prima o poi divergono e le schede finiscono in mano a un
//                   ispettore.
//                   La scheda a valle apre con "Clienti da avvisare",
//                   deduplicati per nome: se lo stesso cliente compare su tre
//                   bolle e' una riga sola, perche' in emergenza si telefona
//                   una volta. Le quantita' NON si sommano fra unita' diverse.
//   Semilavorati .. la scheda espande UN livello di origine
//                   (sl.lavorazione_origine.materie_prime). SCELTA CHIUSA il
//                   19/08/2026: la risalita automatica a piu' livelli non si
//                   fa: copre casi eccezionali e si risolve rifacendo la
//                   ricerca sul lotto del semilavorato. Non riproporla.
//   ESCAPE ........ stampaBackward passa i dati da escProfondo prima di
//                   costruire l'HTML (corretto il 19/08/2026: prima nomi con
//                   < > & mutilavano il documento). Le virgolette NON si
//                   toccano nel testo: parametri_rilevati e' JSON e
//                   formatParametri ci fa JSON.parse sopra.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import {
  GuidaLayout, H, P, Strong, Code, Tasto, Callout, Passo,
  MockFrame, Schermo, SchermoTitolo, Segmenti, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 't0', label: 'Perché esiste' },
  { id: 't1', label: '1. Le due direzioni' },
  { id: 't2', label: '2. Backward: il richiamo' },
  { id: 't3', label: '3. La scheda da stampare' },
  { id: 't4', label: '4. Forward: chi ha ricevuto' },
  { id: 't5', label: '5. La prova annuale' },
  { id: 't6', label: '6. Due cose da sapere' },
  { id: 't7', label: 'Le trappole' },
  { id: 't8', label: 'Domande frequenti' },
];

// ─── Pezzi di schermata ──────────────────────────────────────────────────────

const ChipFinto = ({ children, tono = 'neutro' }) => {
  const c = {
    verde:   { fg: LT.verde,   bg: LT.verdeSoft },
    rosso:   { fg: LT.rosso,   bg: LT.rossoSoft },
    arancio: { fg: LT.arancio, bg: LT.arancioSoft },
    blu:     { fg: LT.blu,     bg: LT.bluSoft },
    viola:   { fg: LT.viola,   bg: LT.violaSoft },
    ciano:   { fg: LT.ciano,   bg: LT.cianoSoft },
    neutro:  { fg: LT.n700,    bg: LT.n100 },
  }[tono];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 999, background: c.bg, color: c.fg,
      fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

const BtnFinto = ({ children, kind = 'neutro' }) => {
  const c = {
    viola:  { bg: LT.viola, fg: '#fff',  bd: LT.viola },
    ciano:  { bg: LT.ciano, fg: '#fff',  bd: LT.ciano },
    neutro: { bg: LT.n0,    fg: LT.n700, bd: LT.n300 },
  }[kind];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      padding: '6px 12px', borderRadius: LT.r.md, fontSize: 12, fontWeight: 600,
      background: c.bg, color: c.fg, border: `1px solid ${c.bd}`, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

// Un anello della catena, usato per disegnare il percorso a monte e a valle.
const Anello = ({ titolo, sotto, tono, ultimo }) => (
  <div style={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
    <div style={{
      flex: 1, border: `1px solid ${LT.n200}`, borderLeft: `3px solid ${tono}`,
      borderRadius: LT.r.md, padding: '9px 12px', background: LT.n0,
    }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: LT.navy }}>{titolo}</div>
      <div style={{ fontSize: 11, color: LT.n500, marginTop: 2 }}>{sotto}</div>
    </div>
    {!ultimo && (
      <div style={{
        width: 26, display: 'flex', alignItems: 'center',
        justifyContent: 'center', color: LT.n400, fontSize: 15,
      }}>→</div>
    )}
  </div>
);

const Catena = ({ anelli, tono }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 0', margin: '16px 0' }}>
    {anelli.map((a, i) => (
      <Anello key={i} titolo={a[0]} sotto={a[1]} tono={tono} ultimo={i === anelli.length - 1} />
    ))}
  </div>
);

const CampoFinto = ({ etichetta, valore, larghezza }) => (
  <div style={{ width: larghezza || '100%' }}>
    <div style={{
      fontSize: 10.5, fontWeight: 700, color: LT.n600,
      textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 3,
    }}>{etichetta}</div>
    <div style={{
      border: `1px solid ${LT.n300}`, borderRadius: LT.r.md, padding: '7px 9px',
      background: LT.n0, fontSize: 13, color: valore ? LT.n900 : LT.n400,
    }}>{valore || '—'}</div>
  </div>
);

export default function TestTracciabilita() {
  return (
    <GuidaLayout
      titolo="Il test di tracciabilità"
      sottotitolo="Da un lotto, risalire a tutto quello che ci è entrato dentro o scendere fino a tutti i clienti che l'hanno ricevuto. È la schermata per cui esiste tutto il resto del modulo, ed è quella da saper usare sotto pressione."
      durata="~ 14 minuti"
      destinatario="Per amministratori · piano TRACE"
      sezioni={SEZIONI}
    >

      {/* t0 */}
      <H id="t0">Perché esiste</H>

      <P>
        Il Regolamento europeo 178/2002 chiede a ogni azienda alimentare di sapere{' '}
        <Strong>da chi ha ricevuto</Strong> e <Strong>a chi ha fornito</Strong>: un passo
        indietro e un passo avanti. Non chiede di averlo scritto da qualche parte, chiede di
        saperlo <em>dimostrare</em>, e in tempi ragionevoli.
      </P>
      <P>
        <Code>Test di tracciabilità</Code>, nel gruppo Verifiche e controlli, è la schermata
        che risponde. Tutto quello che avete registrato — bolle, lavorazioni, fasi,
        spedizioni — serve a rendere possibile questa ricerca. E finché non la provate, non
        sapete se funziona.
      </P>

      <Callout kind="info" titolo="I due momenti in cui si usa">
        <Strong>La prova periodica</Strong>, quella che l'HACCP vi chiede di fare almeno una
        volta l'anno: si sceglie un lotto a caso e si verifica che la catena regga.{' '}
        <Strong>L'emergenza vera</Strong>: un cliente segnala un problema, o un fornitore vi
        avvisa che un suo lotto è stato richiamato. In quel momento non c'è tempo per
        imparare come funziona: per questo la prova annuale non è burocrazia.
      </Callout>

      {/* t1 */}
      <H id="t1" cat="produzione">1. Le due direzioni</H>

      <P>
        In alto ci sono due schede, e la differenza è solo <em>da dove si parte</em>.
      </P>

      <div style={{ margin: '18px 0' }}>
        <Segmenti voci={['Forward · MP → PF', 'Backward · PF → MP']} attivo="Backward · PF → MP" />
      </div>

      <Passo n="1" titolo="Backward — dal prodotto finito alle materie prime">
        <P style={{ margin: '4px 0' }}>
          Si parte da un lotto che avete prodotto e si risale a tutto quello che ci è finito
          dentro. È la direzione del <Strong>richiamo</Strong>: un cliente vi segnala un
          problema su un lotto e dovete capire da dove è arrivato.
        </P>
        <Catena tono={LT.viola} anelli={[
          ['Lotto PF', 'quello che vi hanno segnalato'],
          ['Lavorazione', 'fasi, parametri, operatori'],
          ['Materie prime', 'lotti, fornitori, bolle'],
        ]} />
      </Passo>

      <Passo n="2" titolo="Forward — da una materia prima ai clienti">
        <P style={{ margin: '4px 0' }}>
          Si parte da un lotto di ingrediente e si scende fino a chi ha ricevuto i prodotti
          che lo contengono. È la direzione dell'<Strong>allerta</Strong>: il fornitore vi
          avvisa che il suo lotto è contaminato e dovete sapere chi chiamare.
        </P>
        <Catena tono={LT.ciano} anelli={[
          ['Lotto MP', 'quello richiamato dal fornitore'],
          ['Lavorazioni', 'dove è stato usato'],
          ['Lotti PF', 'cosa ne è uscito'],
          ['Clienti', 'bolle di uscita, quantità'],
        ]} />
      </Passo>

      <Callout kind="tip" titolo="Come ricordarsi quale scegliere">
        Guardate <Strong>cosa avete in mano</Strong>. Se avete il numero di lotto di un
        vostro prodotto, è backward. Se avete il lotto di un ingrediente comprato, è forward.
        La sigla nella scheda lo dice: <Code>MP → PF</Code> parte dalla materia prima,{' '}
        <Code>PF → MP</Code> parte dal prodotto finito.
      </Callout>

      {/* t2 */}
      <H id="t2" cat="manutenzioni">2. Backward: rispondere a un richiamo</H>

      <P>
        Nella casella si scrive il <Strong>lotto del prodotto finito</Strong> — oppure il
        nome del prodotto, che funziona anche. Il segnaposto suggerisce{' '}
        <Code>Es. L.190326 oppure Pane</Code>.
      </P>

      <MockFrame
        url="app.labthree.it — Test di tracciabilità · Backward"
        markers={[
          { n: 1, top: '30%', left: '50%', titolo: 'La ricerca è parziale',
            testo: 'Cerca anche dentro il nome del prodotto e della ricetta: scrivere “Pane” restituisce tutte le lavorazioni di pane.' },
          { n: 2, top: '58%', left: '50%', titolo: 'Un risultato per lavorazione',
            testo: 'Si apre e mostra materie prime, semilavorati, fasi con parametri e operatori, movimenti e bolle di uscita.' },
          { n: 3, top: '85%', left: '80%', titolo: 'Stampa scheda',
            testo: 'Il documento da consegnare. È l’unico punto in cui LabThree produce la prova cartacea della rintracciabilità.' },
        ]}
      >
        <Schermo>
          <SchermoTitolo
            titolo="Test di tracciabilità"
            sottotitolo="Verifica forward (MP → PF) e backward (PF → MP) con stampa della scheda"
          />
          <div style={{ marginBottom: 12 }}>
            <Segmenti voci={['Forward · MP → PF', 'Backward · PF → MP']} attivo="Backward · PF → MP" />
          </div>

          <div style={{
            background: LT.n0, border: `1px solid ${LT.n200}`,
            borderRadius: LT.r.md, padding: 12, marginBottom: 11,
          }}>
            <div style={{ fontSize: 12, color: LT.n600, marginBottom: 9 }}>
              Cerca per <strong>lotto PF</strong> o <strong>nome prodotto</strong> per
              risalire a tutte le materie prime utilizzate.
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <CampoFinto etichetta="Lotto prodotto finito" valore="L. 23 PT" />
              </div>
              <BtnFinto kind="viola">Cerca</BtnFinto>
            </div>
          </div>

          <div style={{
            background: LT.n0, border: `1px solid ${LT.n200}`,
            borderLeft: `3px solid ${LT.viola}`, borderRadius: LT.r.md, padding: 12,
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'flex-start', gap: 10, flexWrap: 'wrap',
            }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: LT.navy }}>
                  Prosciutto crudo stagionato
                </div>
                <div style={{ fontSize: 11.5, color: LT.n600, fontFamily: LT.mono, marginTop: 2 }}>
                  L. 23 PT · lav. #214 · 12/06/2026
                </div>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 7 }}>
                  <ChipFinto tono="blu">3 materie prime</ChipFinto>
                  <ChipFinto tono="verde">9 fasi</ChipFinto>
                  <ChipFinto tono="viola">4 bolle di uscita</ChipFinto>
                  <ChipFinto tono="arancio">Conto terzi · Protein Srl</ChipFinto>
                </div>
              </div>
              <BtnFinto>Stampa scheda</BtnFinto>
            </div>
          </div>
        </Schermo>
      </MockFrame>

      <Callout kind="tip" titolo="Non serve ricordarsi il lotto esatto">
        La ricerca è <Strong>parziale</Strong> e guarda anche dentro il nome del prodotto e
        della ricetta. Basta un pezzo del codice — <Code>0326</Code> invece di{' '}
        <Code>L.190326</Code> — oppure direttamente <Code>Pane</Code>, e vi restituisce tutte
        le lavorazioni di pane fra cui scegliere. È il modo in cui la userete davvero: quando
        arriva la telefonata, il numero di lotto scritto giusto non ce l'ha quasi mai
        nessuno. Se invece i risultati sono troppi, allungate il testo cercato.
      </Callout>

      <P>
        Ogni risultato si apre e mostra, in ordine: le <Strong>materie prime</Strong> con
        lotto del fornitore, lotto interno, fornitore, bolla di ingresso e scadenza; i{' '}
        <Strong>semilavorati</Strong> con la lavorazione da cui vengono; le{' '}
        <Strong>fasi</Strong> con date, operatore, parametri rilevati, note e movimenti di
        ubicazione; e le <Strong>bolle di uscita</Strong> con i clienti.
      </P>

      {/* t3 */}
      <H id="t3" cat="formazione">3. La scheda da stampare</H>

      <P>
        <Tasto>Stampa scheda</Tasto> apre una pagina pronta da stampare o da salvare in PDF.
        È intestata con i vostri dati e il vostro logo, e riporta <Strong>chi ha fatto la
        ricerca e quando</Strong> — che è la parte che la trasforma da elenco a documento.
      </P>

      <div style={{
        border: `1px solid ${LT.n200}`, borderRadius: LT.r.md,
        padding: '16px 18px', margin: '18px 0', background: LT.n0,
      }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: LT.n500, letterSpacing: 0.5, marginBottom: 10 }}>
          COSA CONTIENE LA SCHEDA
        </div>
        {[
          ['Intestazione', 'logo e ragione sociale, data della ricerca, lotto cercato, nome di chi l’ha fatta'],
          ['Dati della lavorazione', 'prodotto, lotto, ricetta, data di produzione, quantità, stato'],
          ['Materie prime', 'ingrediente, categoria, lotto fornitore e interno, fornitore, bolla, scadenza'],
          ['Semilavorati', 'prodotto, lotto, quantità, lavorazione di origine e le sue materie prime'],
          ['Fasi', 'ordine, inizio e fine, operatore, parametri rilevati, note, movimenti di ubicazione'],
          ['Movimenti', 'data e ora, tipo, ubicazione, quantità, fase, motivazione degli scarti'],
          ['Bolle di uscita', 'numero, data, cliente, quantità'],
        ].map(([t, d], i) => (
          <div key={i} style={{
            display: 'flex', gap: 12, padding: '7px 0', flexWrap: 'wrap',
            borderBottom: i < 6 ? `1px solid ${LT.n150}` : 'none',
          }}>
            <span style={{ flex: '0 0 150px', fontSize: 13.5, fontWeight: 700, color: LT.navy }}>{t}</span>
            <span style={{ flex: '1 1 260px', fontSize: 13.5, color: LT.n700, lineHeight: 1.5 }}>{d}</span>
          </div>
        ))}
      </div>

      <Callout kind="tip" titolo="Salvatela in PDF, non solo su carta">
        Nella finestra di stampa scegliete <Tasto>Salva come PDF</Tasto> invece della
        stampante. Serve a due cose: allegarla a una mail per il cliente o l'ASL nel giro di
        un minuto, e conservare la prova di <em>quando</em> l'avete fatta — la data della
        ricerca è stampata sopra.
      </Callout>

      <Callout kind="info" titolo="Anche il forward ha la sua scheda, ed è fatta apposta per l'emergenza">
        Il pulsante <Tasto>Stampa scheda</Tasto> c'è su tutti e due i risultati, ma i due
        documenti servono a cose diverse. Quello a monte racconta{' '}
        <em>com'è stato fatto</em> un lotto. Quello a valle apre con la sezione{' '}
        <Strong>Clienti da avvisare</Strong>, e sotto dice quanto prodotto è{' '}
        <Strong>ancora in magazzino da bloccare</Strong>. Nei primi dieci minuti di
        un'allerta servono solo quelle due informazioni.
      </Callout>

      {/* t4 */}
      <H id="t4" cat="temperature">4. Forward: sapere chi chiamare</H>

      <P>
        Si parte da un lotto di materia prima: <Strong>lotto del fornitore</Strong>,{' '}
        <Strong>lotto interno</Strong> o <Strong>nome dell'ingrediente</Strong>. Il
        segnaposto suggerisce <Code>Es. F230326, LI-2024-001 oppure Farina</Code>.
      </P>
      <P>
        Il risultato scende tutta la catena e <Strong>arriva fino ai clienti</Strong>: per
        ogni lavorazione che ha usato quel lotto mostra i lotti di prodotto finito che ne
        sono usciti, quanto ne è stato spedito e quanto ne resta, e le bolle di uscita con
        nome del cliente, data e quantità.
      </P>

      <Callout kind="info" titolo="È la risposta alla telefonata del fornitore">
        Quando un fornitore vi comunica che il suo lotto X ha un problema, la domanda che vi
        fate è una sola: <em>a chi è finito?</em> Il forward risponde in pochi secondi
        elencando i clienti e le quantità. Senza, quella risposta significa ore di ricerca
        fra bolle cartacee, e con un margine di errore che nessuno vuole in quel momento.
      </Callout>

      <P>
        Su ogni lotto di prodotto finito compaiono anche <Strong>quanto è stato spedito</Strong>{' '}
        e <Strong>quanto è ancora in magazzino</Strong>. È l'informazione che decide l'azione:
        quello che è ancora da voi si blocca in cella, quello che è uscito richiede una
        telefonata.
      </P>

      {/* t5 */}
      <H id="t5" cat="formazione">5. La prova annuale</H>

      <P>
        Farla è semplice, e serve a scoprire i buchi quando non costano niente. Un giro
        completo dura una decina di minuti.
      </P>

      <Passo n="1" titolo="Scegliete un lotto a caso, non il più bello">
        <P style={{ margin: '4px 0' }}>
          Meglio uno di qualche mese fa, di un prodotto complesso, magari con un
          semilavorato dentro. Un lotto scelto per comodità dimostra poco.
        </P>
      </Passo>

      <Passo n="2" titolo="Backward, e stampate la scheda">
        <P style={{ margin: '4px 0' }}>
          Guardatela come la guarderebbe un ispettore: i fornitori ci sono tutti? Le fasi
          hanno gli operatori? I parametri sono compilati o pieni di trattini?
        </P>
      </Passo>

      <Passo n="3" titolo="Forward su una delle materie prime che avete appena trovato">
        <P style={{ margin: '4px 0' }}>
          Chiude il cerchio: dal prodotto siete risaliti all'ingrediente, dall'ingrediente
          scendete fino ai clienti. Se le due direzioni si parlano, la catena regge.
        </P>
      </Passo>

      <Passo n="4" titolo="Scrivete che l'avete fatta">
        <P style={{ margin: '4px 0' }}>
          Il PDF salvato, con la sua data, è già la prova. Conservatelo dove tenete i
          documenti dell'autocontrollo — o caricatelo in{' '}
          <Code>Documenti HACCP</Code>, così sta insieme al resto.
        </P>
      </Passo>

      {/* t6 */}
      <H id="t6">6. Due cose da sapere prima, non dopo</H>

      <P>
        Meglio saperle adesso che scoprirle con l'ispettore davanti.
      </P>

      <Callout kind="info" titolo="I semilavorati si risalgono un livello per volta">
        Se il prodotto contiene un semilavorato, la scheda mostra la lavorazione da cui viene{' '}
        <em>e</em> le sue materie prime. Se quel semilavorato ne conteneva a sua volta un
        altro, si rifà il backward <Strong>sul lotto del semilavorato</Strong> e si scende di
        un altro gradino. È una scelta: le catene a tre livelli sono rare, e una risalita
        automatica renderebbe la scheda molto più difficile da leggere proprio nel momento in
        cui va letta in fretta. Due ricerche invece di una, stesso risultato.
      </Callout>

      <Callout kind="warn" titolo="Il test trova solo quello che è stato registrato">
        È ovvio e va detto lo stesso: una consegna non registrata non compare, e la catena si
        interrompe senza che niente lo segnali. La qualità di questa schermata è esattamente
        la qualità delle registrazioni quotidiane — è il motivo per cui vale la pena
        registrare le bolle di uscita il giorno stesso.
      </Callout>

      {/* t7 */}
      <H id="t7">Le trappole</H>

      <Callout kind="warn" titolo="Backward non trova le lavorazioni aperte">
        Il lotto di prodotto finito nasce alla chiusura della lavorazione. Una produzione
        ancora aperta non ha un lotto PF, quindi non è cercabile all'indietro.
      </Callout>

      <Callout kind="warn" titolo="Due lavorazioni possono avere lo stesso lotto">
        Il lotto è derivato dalla data: due produzioni avviate lo stesso giorno lo
        condividono, e la ricerca le restituisce entrambe. Non è un errore — si distinguono
        dal numero di lavorazione e dal prodotto, e in un richiamo vanno considerate tutte e
        due finché non si è certi di quale sia.
      </Callout>

      <Callout kind="warn" titolo="Un risultato vuoto non vuol dire che il lotto non esiste">
        Vuol dire che quel testo non compare da nessuna parte. Prima di allarmarsi conviene
        provare con una parte del codice invece che con tutto, o col nome del prodotto: gli
        errori di battitura e gli spazi di troppo sono la causa più comune del{' '}
        <em>Nessuna corrispondenza trovata</em>.
      </Callout>

      {/* t8 */}
      <H id="t8">Domande frequenti</H>

      <div style={{ marginTop: 8 }}>
        <FAQ q="Ogni quanto va fatto il test?">
          Il riferimento comune è <Strong>almeno una volta l'anno</Strong>, ed è quello che
          chiede la maggior parte dei manuali di autocontrollo. Se il vostro manuale indica
          una frequenza diversa, vale quella: è il vostro documento a stabilirlo, non il
          programma.
        </FAQ>

        <FAQ q="Quanto tempo ho per rispondere a un'autorità?">
          Non c'è un termine unico scritto in una norma: dipende dalla situazione e da chi
          chiede. Il criterio che conta è che la risposta sia <em>senza indugio</em>, cioè
          nel giro di ore e non di giorni. È esattamente il motivo per cui vale la pena
          provare la ricerca a freddo: sapere di poterla fare in due minuti cambia il modo in
          cui si affronta quella telefonata.
        </FAQ>

        <FAQ q="Che differenza c'è fra le due schede stampate?">
          Quella <Strong>a monte</Strong> (backward) racconta come è stato fatto un lotto:
          materie prime, fasi, parametri, movimenti. Quella <Strong>a valle</Strong>
          (forward) è pensata per l'emergenza: apre con l'elenco dei{' '}
          <Strong>clienti da avvisare</Strong>, ciascuno una riga sola anche se ha ricevuto
          più spedizioni, e dice quanto prodotto è ancora in magazzino da bloccare. Stessa
          impaginazione, due scopi diversi.
        </FAQ>

        <FAQ q="La scheda vale come documento ufficiale?">
          È un documento che voi producete dal vostro registro, e come tale è la prova della
          vostra rintracciabilità: porta i vostri dati, la data della ricerca e il nome di
          chi l'ha fatta. Non sostituisce i DDT né i documenti fiscali, che restano quelli
          emessi dal vostro gestionale.
        </FAQ>

        <FAQ q="Un operatore può fare il test?">
          La schermata sta fra le Verifiche e controlli ed è pensata per chi gestisce
          l'autocontrollo. È di sola lettura: non modifica niente, quindi non c'è rischio
          nel farla provare a qualcun altro — anzi, in un'emergenza è utile che sappia farla
          più di una persona.
        </FAQ>

        <FAQ q="Nella scheda alcune fasi hanno i parametri vuoti.">
          Vuol dire che in quella fase non sono stati registrati valori. Non è un difetto
          della stampa: è una lacuna nella registrazione, e la scheda ve la mostra proprio
          perché la vediate. È uno dei motivi per cui la prova annuale è utile.
        </FAQ>

        <FAQ q="Il prodotto era in conto lavorazione: si vede?">
          Sì, il contrassegno <ChipFinto tono="arancio">Conto terzi</ChipFinto> col nome del
          committente compare sul risultato. In un richiamo è un'informazione che cambia chi
          va avvisato: la merce non è vostra, e il proprietario va coinvolto.
        </FAQ>
      </div>

      <div style={{
        marginTop: 40, padding: '18px 20px', background: LT.violaSoft,
        borderRadius: LT.r.md, display: 'flex', gap: 14, alignItems: 'flex-start',
      }}>
        <Ico d={I.info} size={20} stroke={LT.viola} style={{ marginTop: 2 }} />
        <div>
          <div style={{ fontWeight: 700, color: LT.viola, fontSize: 14, marginBottom: 4 }}>
            Se serve davvero, l'ordine è questo
          </div>
          <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.6 }}>
            <Strong>Forward</Strong> sul lotto segnalato per sapere <em>chi</em> ha ricevuto
            e <em>quanto</em>. Bloccate quello che è ancora in magazzino. Poi{' '}
            <Strong>backward</Strong> sui lotti coinvolti per capire <em>da dove</em> arriva
            il problema, e stampate la scheda. Infine aprite una{' '}
            <Strong>non conformità</Strong>: è lì che quella storia deve restare scritta.
          </div>
        </div>
      </div>

    </GuidaLayout>
  );
}
